import { IcoRemove } from '@/utils/icons'
import Modal from '@common/Modal'
import TextField from '../../components/TextField/TextField'
import { useEffect, useMemo, useState } from 'react'
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton'
import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionGroupApi } from '@/api'
import SelectField from '../../components/SelectField/SelectField'
import { fetchColleagues } from '../../tabs/CourseSettingsTab/apiService'
import cn from './index.module.css'

const CreateLearningGroup = ({
  isVisible,
  directionId,
  onDismiss = () => {},
  onCreate = (group) => {},
}: {
  isVisible: boolean
  directionId: number
  onDismiss: () => void
  onCreate: (folder: IGroup) => void
}) => {
  return (
    <Modal
      active={isVisible}
      setActive={onDismiss}
      // @ts-ignore
      className={cn.modalWrapper}
    >
      <CreateLearningGroupBody
        directionId={directionId}
        onCreate={onCreate}
        onDismiss={onDismiss}
      />
    </Modal>
  )
}

const CreateLearningGroupBody = ({
  directionId,
  onDismiss = () => {},
  onCreate = (group) => {},
}: {
  directionId: number
  onDismiss: () => void
  onCreate: (group: IGroup) => void
}) => {
  const addAlert = useAddAlert()
  const [isLoading, setIsLoading] = useState(false)
  const [colleagues, setColleagues] = useState<IColleague[]>([])
  const [select, setSelect] = useState<ISelect[]>([])
  const [lead, setLead] = useState<number | null>(null)
  const [search, setSearch] = useState<string>('')
  const [name, setName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string[]>([])

  useEffect(() => {
    setIsLoading(true)
    const delayDebounce = setTimeout(async () => {
      async function fetchData() {
        let response = await fetchColleagues(search)
        setSelect(
          response
            ?.filter((item) => !item?.roles?.includes('parent'))
            ?.map((item) => ({
              label: item.name,
              value: item.id.toString(),
            })),
        )
        setColleagues(response)
        setIsLoading(false)
      }

      fetchData()
    }, 300)

    return () => clearTimeout(delayDebounce)
  }, [search])

  const createGroup = () => {
    if (isSaving) return
    if (!lead) {
      setError(['lead'])
      return
    }
    if (!name) {
      setError(['name'])
      return
    }
    setIsSaving(true)

    const data = {
      direction_id: directionId,
      teacher_id: lead,
      name: name,
    }
    directionGroupApi
      .add({ data })
      .then((res) => {
        if (res?.data?.data?.id) {
          addAlert({ title: 'Группа создана!' })
          onCreate(res?.data?.data)
        } else {
          addAlert({
            type: 'error',
            title:
              'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
          })
        }
      })
      .catch((error) => {
        addAlert({
          type: 'error',
          title:
            'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
        })
      })
      .finally(() => setIsSaving(false))
  }

  const isNew = useMemo(() => {
    if (!lead) return false
    const user = colleagues.find((item) => item.id === lead)
    if (!user) return false
    return (
      !user?.isTeacher ||
      !user?.directions?.some((item) => item?.id === directionId)
    )
  }, [colleagues, lead])

  return (
    <div className={cn.modalContainer}>
      <div className={cn.modalHeader}>
        <div className={cn.modalTitleContainer}>
          <span className={cn.modalHeaderTitle}>
            Создание группы/потока обучения
          </span>
        </div>
        <div className={cn.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={cn.content}>
        <SelectField
          label="Выберите сотрудника"
          placeholder="сотрудник"
          value={lead?.toString()}
          onChange={(value) => setLead(Number(value))}
          // @ts-ignore
          options={select}
          className={cn.select}
          isInvalid={error.includes('lead')}
          onSearch={(value) => setSearch(value)}
          loading={isLoading}
        />
        {isNew && (
          <div className={cn.additionalInfo}>
            У этого сотрудника нет роли педагог и/или доступа к этому курсу. При
            создании с ним группы система автоматически добавит ему эту роль и
            доступ к курсу.
          </div>
        )}
        <TextField
          label="Введите название группы"
          value={name}
          onChange={setName}
          className={cn.textField}
          isInvalid={error.includes('name')}
        />
      </div>
      <div className={cn.footer}>
        {isSaving && (
          <Spin
            indicator={
              <LoadingOutlined
                spin
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
          />
        )}
        <SecondaryButton onClick={createGroup} disabled={isSaving}>
          Создать
        </SecondaryButton>
      </div>
    </div>
  )
}

export default CreateLearningGroup
